<template>
  <transition
    name="unpin-slide"
    @after-leave="afterLeave"
  >
    <div v-if="!isLeaving" class="info-card" :class="{ expanded: expanded.health, collapsed: !expanded.health }">
      <div class="info-card-content">
        <!-- Collapsed state -->
        <div v-if="!expanded.health" class="collapsed-content">
          <div class="info-row">
            <div class="icon-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM16 16H14.27L16 14.27V16ZM16 13H15.15L12.15 16H10.53L13.53 13H11.41L8.41 16H6.8L9.8 13H7.68L4.68 16H2.84L5.84 13H3.72L2 14.72V2H16V13Z" fill="#25B8C2"/>
                <path d="M7.67 8L9.67 10L14.75 4.91L13.34 3.5L9.67 7.17L7.67 5.17L3.25 9.59L4.66 11L7.67 8Z" fill="#25B8C2"/>
              </svg>
            </div>
            <p class="info-text-title">Health:</p>
            <div class="flex flex-row items-center gap-2 ml-auto">
              <div @click.stop="toggleCard('health')" class="action-icon">
                <img src="@/assets/cardActions/expand.svg" alt="Expand" />
              </div>
              <div class="drag-handle action-icon">
                <img src="@/assets/cardActions/move.svg" alt="Move" />
              </div>
            </div>
          </div>
        </div>

        <!-- Expanded state -->
        <div v-else class="health-card-expanded">
          <!-- Header row: icon + title + pin -->
          <div class="health-header">
            <div class="icon-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM16 16H14.27L16 14.27V16ZM16 13H15.15L12.15 16H10.53L13.53 13H11.41L8.41 16H6.8L9.8 13H7.68L4.68 16H2.84L5.84 13H3.72L2 14.72V2H16V13Z" fill="#25B8C2"/>
                <path d="M7.67 8L9.67 10L14.75 4.91L13.34 3.5L9.67 7.17L7.67 5.17L3.25 9.59L4.66 11L7.67 8Z" fill="#25B8C2"/>
              </svg>
            </div>
            <p class="info-text-title">Vital & Measurements:</p>
            <div class="header-action-icons">
              <div v-if="!isCardPinned" class="action-icon" @click.stop="handlePinCard">
                <img src="@/assets/cardActions/pin.svg" alt="Pin" />
              </div>
              <div v-if="isCardPinned" class="action-icon" @click.stop="startUnpin">
                <img src="@/assets/cardActions/unpin.svg" alt="Unpin" />
              </div>
            </div>
          </div>

          <!-- Content: sub-section cards -->
          <div class="health-content">
            <div v-if="isLoading" class="loading-state">
              <p class="info-text">Loading vitals...</p>
            </div>
            <div v-else-if="latestHealthInfo" class="vital-sub-cards">
              <!-- Vital - Measurements -->
              <div class="vital-sub-card">
                <div class="vital-sub-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="4" fill="#F2F2F2"/>
                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM12 6C10.34 6 9 7.34 9 9C9 10.66 10.34 12 12 12C13.66 12 15 10.66 15 9C15 7.34 13.66 6 12 6ZM11 9.5H10V8.5H11V9.5ZM12.5 9.5H11.5V8.5H12.5V9.5ZM14 9.5H13V8.5H14V9.5Z" fill="#25B8C2"/>
                  </svg>
                </div>
                <div class="vital-sub-body">
                  <p class="vital-sub-title">Vital - Measurements</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Height:</span> {{ latestHealthInfo.height || 'N/A' }}</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Weight:</span> {{ latestHealthInfo.weight || 'N/A' }} lb</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">BMI:</span> {{ latestHealthInfo.bmi || 'N/A' }}</p>
                </div>
                <div class="vital-sub-variable-btn" @click="openVariableDialog(measurementVariables)">
                  <img src="@/assets/cardActions/pt_variable.svg" alt="Variable Properties" />
                  <span>Variable Properties</span>
                </div>
              </div>

              <!-- Vital - Heart Rate -->
              <div class="vital-sub-card">
                <div class="vital-sub-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
                    <path d="M10 18C9.7 18 9.4125 17.9458 9.1375 17.8375C8.8625 17.7292 8.61667 17.5667 8.4 17.35L1.7 10.625C1.11667 10.0417 0.6875 9.375 0.4125 8.625C0.1375 7.875 0 7.09167 0 6.275C0 4.55833 0.558333 3.08333 1.675 1.85C2.79167 0.616667 4.18333 0 5.85 0C6.65 0 7.40417 0.158333 8.1125 0.475C8.82083 0.791667 9.45 1.23333 10 1.8C10.5333 1.23333 11.1542 0.791667 11.8625 0.475C12.5708 0.158333 13.325 0 14.125 0C15.7917 0 17.1875 0.616667 18.3125 1.85C19.4375 3.08333 20 4.55 20 6.25C20 7.06667 19.8583 7.85 19.575 8.6C19.2917 9.35 18.8667 10.0167 18.3 10.6L11.575 17.35C11.3583 17.5667 11.1167 17.7292 10.85 17.8375C10.5833 17.9458 10.3 18 10 18ZM11 5C11.1667 5 11.325 5.04167 11.475 5.125C11.625 5.20833 11.7417 5.31667 11.825 5.45L13.525 8H17.675C17.7917 7.71667 17.8792 7.42917 17.9375 7.1375C17.9958 6.84583 18.025 6.55 18.025 6.25C17.9917 5.1 17.6083 4.1125 16.875 3.2875C16.1417 2.4625 15.225 2.05 14.125 2.05C13.6083 2.05 13.1125 2.15 12.6375 2.35C12.1625 2.55 11.75 2.84167 11.4 3.225L10.725 3.95C10.6417 4.05 10.5333 4.12917 10.4 4.1875C10.2667 4.24583 10.1333 4.275 10 4.275C9.86667 4.275 9.73333 4.24583 9.6 4.1875C9.46667 4.12917 9.35 4.05 9.25 3.95L8.575 3.225C8.225 2.84167 7.81667 2.54167 7.35 2.325C6.88333 2.10833 6.38333 2 5.85 2C4.75 2 3.83333 2.42083 3.1 3.2625C2.36667 4.10417 2 5.1 2 6.25C2 6.55 2.025 6.84583 2.075 7.1375C2.125 7.42917 2.20833 7.71667 2.325 8H7C7.16667 8 7.325 8.04167 7.475 8.125C7.625 8.20833 7.74167 8.31667 7.825 8.45L8.7 9.75L10.05 5.7C10.1167 5.5 10.2375 5.33333 10.4125 5.2C10.5875 5.06667 10.7833 5 11 5ZM11.3 8.25L9.95 12.3C9.88333 12.5 9.75833 12.6667 9.575 12.8C9.39167 12.9333 9.19167 13 8.975 13C8.80833 13 8.65 12.9583 8.5 12.875C8.35 12.7917 8.23333 12.6833 8.15 12.55L6.45 10H3.9L9.825 15.925C9.85833 15.9583 9.8875 15.9792 9.9125 15.9875C9.9375 15.9958 9.96667 16 10 16C10.0333 16 10.0625 15.9958 10.0875 15.9875C10.1125 15.9792 10.1417 15.9583 10.175 15.925L16.075 10H13C12.8333 10 12.675 9.95833 12.525 9.875C12.375 9.79167 12.25 9.68333 12.15 9.55L11.3 8.25Z" fill="#25B8C2"/>
                  </svg>
                </div>
                <div class="vital-sub-body">
                  <p class="vital-sub-title">Vital - Heart Rate</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Heart Rate (BPM):</span> {{ latestHealthInfo.heartRate || 'N/A' }}</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Respiration Rate (BPM):</span> {{ latestHealthInfo.respiratoryRate || latestHealthInfo.respirationRate || 'N/A' }}</p>
                </div>
                <div class="vital-sub-variable-btn" @click="openVariableDialog(heartRateVariables)">
                  <img src="@/assets/cardActions/pt_variable.svg" alt="Variable Properties" />
                  <span>Variable Properties</span>
                </div>
              </div>

              <!-- Vital - Temperature -->
              <div class="vital-sub-card">
                <div class="vital-sub-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="4" fill="#F2F2F2"/>
                    <path d="M15 13V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V13C7.79 13.91 7 15.37 7 17C7 19.76 9.24 22 12 22C14.76 22 17 19.76 17 17C17 15.37 16.21 13.91 15 13ZM11 11V5C11 4.45 11.45 4 12 4C12.55 4 13 4.45 13 5V6H12V7H13V9H12V10H13V11H11Z" fill="#25B8C2"/>
                  </svg>
                </div>
                <div class="vital-sub-body">
                  <p class="vital-sub-title">Vital - Temperature</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Temperature (°F):</span> {{ latestHealthInfo.temperature || 'N/A' }}</p>
                </div>
              </div>

              <!-- Vital - Blood Condition -->
              <div class="vital-sub-card">
                <div class="vital-sub-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
                    <path d="M13.66 5.65L8 0L2.34 5.65C0.78 7.21 0 9.29 0 11.29C0 13.29 0.78 15.4 2.34 16.96C3.9 18.52 5.95 19.31 8 19.31C10.05 19.31 12.1 18.52 13.66 16.96C15.22 15.4 16 13.29 16 11.29C16 9.29 15.22 7.21 13.66 5.65ZM2 11.65C2.01 9.65 2.62 8.38 3.76 7.25L8 2.92L12.24 7.3C13.38 8.42 13.99 9.65 14 11.65H2Z" fill="#25B8C2"/>
                  </svg>
                </div>
                <div class="vital-sub-body">
                  <p class="vital-sub-title">Vital - Blood Condition</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Blood Pressure:</span></p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Systolic:</span> {{ latestHealthInfo.systolic || 'N/A' }}  <span class="vital-sub-label">Diastolic:</span> {{ latestHealthInfo.diastolic || 'N/A' }}</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Oxygen Saturation (%):</span> {{ latestHealthInfo.oxygenSaturation || 'N/A' }}</p>
                </div>
                <div class="vital-sub-variable-btn" @click="openVariableDialog(bloodConditionVariables)">
                  <img src="@/assets/cardActions/pt_variable.svg" alt="Variable Properties" />
                  <span>Variable Properties</span>
                </div>
              </div>

              <!-- Vital - Pain Level -->
              <div class="vital-sub-card">
                <div class="vital-sub-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
                    <path d="M8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8ZM8 2C9.1 2 10 2.9 10 4C10 5.1 9.1 6 8 6C6.9 6 6 5.1 6 4C6 2.9 6.9 2 8 2ZM14.39 10.56C12.71 9.7 10.53 9 8 9C5.47 9 3.29 9.7 1.61 10.56C0.61 11.07 0 12.1 0 13.22V20H2V13.22C2 12.84 2.2 12.5 2.52 12.34C3.71 11.73 5.63 11 8 11C8.76 11 9.47 11.07 10.13 11.2L8.58 14.5H5.75C4.23 14.5 3 15.73 3 17.25C3 18.77 4.23 20 5.75 20H14C15.1 20 16 19.1 16 18V13.22C16 12.1 15.39 11.07 14.39 10.56ZM6.94 18H5.75C5.34 18 5 17.66 5 17.25C5 16.84 5.34 16.5 5.75 16.5H7.64L6.94 18ZM14 18H9.15L12.09 11.73C12.63 11.93 13.1 12.14 13.49 12.34C13.8 12.5 14 12.84 14 13.22V18Z" fill="#25B8C2"/>
                  </svg>
                </div>
                <div class="vital-sub-body">
                  <p class="vital-sub-title">Vital - Pain Level</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Pain Level (0-10):</span> {{ latestHealthInfo.painLevel || 'N/A' }}</p>
                  <p class="vital-sub-value"><span class="vital-sub-label">Cause of Pain:</span> {{ latestHealthInfo.causeOfPain || 'N/A' }}</p>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <p class="info-text">There is no health information recorded for this patient yet.</p>
            </div>
          </div>
        </div>
      </div>
      <div class="card-expand" :class="{ 'justify-end': !showFooter, 'justify-between': showFooter }">
        <FoldableButton v-if="showFooter" customized_class="drag-handle" text="Move">
          <template #prefix>
            <img src="@/assets/cardActions/move.svg" alt="Move" />
          </template>
        </FoldableButton>
        <div class="flex flex-row gap-1 justify-end">
          <FoldableButton text="Add new" @click="handleAddNew">
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1V13M1 7H13" stroke="#1E4670" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </template>
          </FoldableButton>
        </div>
      </div>
    </div>
  </transition>

  <PatientVariableDialog
    v-model:visible="showPtVariablesDialog"
    :ptVariables="currentPtVariables"
    :patientId="patientId"
  />
</template>

<script>
export default { inheritAttrs: false }
</script>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useCardActions } from '@/composables/useCardActions'
import PatientVariableDialog from './PatientVariableDialog.vue'

// Define props
const props = defineProps({
  patient: {
    type: String,
    default: ''
  },
  cardData: {
    type: Object,
    default: () => ({})
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  context: {
    type: String,
    default: 'pegboard'
  },
  healthInfo: {
    type: Object,
    default: null
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  externalVariableDialog: {
    type: Boolean,
    default: false
  }
})

// Define emits
const emit = defineEmits(['pin-card', 'unpin-card', 'add-new', 'open-variable-dialog'])

const store = useStore()

// Use the card actions composable
const { isCardPinned, handlePinCard, handleUnpinCard } = useCardActions(props, emit)

const isLeaving = ref(false)

const startUnpin = () => {
  if(props.showFooter){
    isLeaving.value = true
  } else {
    handleUnpinCard()
  }
}

const afterLeave = () => {
  emit('unpin-card', props.cardData)
}

const expanded = ref({
  health: true,
})

const toggleCard = (cardType) => {
  expanded.value[cardType] = !expanded.value[cardType]
}

// Get health info from prop or fall back to store (for pegboard sync)
const latestHealthInfo = computed(() => {
  return props.healthInfo || store.state.myPatients?.patientInfo?.currentPatientHealthInfo || null
})

// Handle add new button click
const handleAddNew = () => {
  emit('add-new')
}

// Patient variable dialog
const showPtVariablesDialog = ref(false)
const currentPtVariables = ref([])

// HealthInfoCard receives currentPatientId directly (not roomId)
const patientId = computed(() => {
  return props.patient || '';
})

// Variables per sub-card matching API definitions
const measurementVariables = ['pt_height', 'pt_weight']
const heartRateVariables = ['pt_pulse']
const bloodConditionVariables = ['pt_bp', 'pt_spo2']

const openVariableDialog = (variables) => {
  currentPtVariables.value = variables
  if (!props.externalVariableDialog) {
    showPtVariablesDialog.value = true
  }
  emit('open-variable-dialog', variables)
}
</script>

<style scoped>
/* Expanded card footer visibility with hover expand animation */
.card-expand {
  display: flex;
  align-items: center;
  gap: 16px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  padding-top: 0;
  margin-top: 0;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  border-top-color: transparent;
  pointer-events: none;
  transition: max-height 300ms linear,
              opacity 300ms linear,
              padding-top 300ms linear,
              padding-bottom 300ms linear,
              margin-top 300ms linear,
              border-top-color 300ms linear;
}

.info-card.expanded:hover .card-expand {
  max-height: 80px;
  opacity: 1;
  padding-top: 10px;
  padding-bottom: 4px;
  margin-top: 10px;
  border-top-color: var(--DividerGray, #C4C4C4);
  pointer-events: auto;
}

.unpin-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.unpin-slide-leave-to {
  transform: translateX(-100%) scale(0.95);
  opacity: 0;
}

.info-card {
  background-color: white;
  border-radius: 5px;
  padding: 12px 16px;
  box-shadow: 0px 1px 25px 0px rgba(74, 74, 74, 0.15);
}

.info-card-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-container {
  background-color: #f2f2f2;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.info-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333333;
  letter-spacing: 0.25px;
  line-height: 20px;
}

.action-icon {
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  padding: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-icon:hover,
.expand-action:hover {
  background-color: #E5F1FB;
}

/* Health card expanded state */
.health-card-expanded {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  flex: 1;
}

/* Header row: icon + title + pin */
.health-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.health-header .info-text-title {
  flex: 1;
}

.header-action-icons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: auto;
}

.health-content {
  padding: 0;
}

.info-text-title {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #666;
}

/* Vital sub-section cards */
.vital-sub-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vital-sub-card {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  border-radius: 5px;
  background: var(--Cool-White, #F9FBFD);
  padding: 12px 16px;
  position: relative;
}

.vital-sub-variable-btn {
  position: absolute;
  top: 4px;
  right: 8px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 26px;
  height: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  cursor: pointer;
  border-radius: 5px;
  transition: width 0.25s ease, padding 0.25s ease, gap 0.25s ease, background-color 0.2s;
  gap: 0;
}

.vital-sub-variable-btn:hover {
  padding: 2px 4px;
  justify-content: flex-start;
  gap: 6px;
  width: 160px;
  background-color: #E5F1FB;
}

.vital-sub-variable-btn span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.2s ease;
  transition-delay: 0.05s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.vital-sub-variable-btn:hover span {
  opacity: 1;
}

.vital-sub-variable-btn img {
  flex-shrink: 0;
}

.vital-sub-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.vital-sub-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.vital-sub-title {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 500;
  color: #666;
  line-height: 12px;
  letter-spacing: 0.25px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.vital-sub-value {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #333;
  line-height: 16px;
  letter-spacing: 0.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.vital-sub-label {
  font-weight: 700;
  color: #666;
}

.loading-state {
  padding: 8px 0;
  text-align: center;
  color: #666666;
  font-style: italic;
}

.empty-state {
  padding: 8px 0;
  text-align: center;
  color: #999999;
  font-style: italic;
}

/* Expand buttons */
.expand-action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1B79A0;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 5px;
}

.expand-action span {
  color: var(--color-Darkblue-000, #1E4670);
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-decoration-line: underline;
}

.add-new-expand {
  display: flex;
  align-items: center;
  width: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.25s ease, padding 0.25s ease, gap 0.25s ease;
  gap: 0;
}

.add-new-expand:hover {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
  width: 92px;
}

.add-new-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.2s ease;
  transition-delay: 0.05s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.add-new-expand:hover span {
  opacity: 1;
}

.add-new-expand svg {
  flex-shrink: 0;
}

/* Move expand button */
.move-expand {
  display: flex;
  align-items: center;
  width: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.4s ease, padding 0.4s ease, gap 0.4s ease;
  gap: 0;
}

.move-expand:hover {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
  width: 78px;
}

.move-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.3s ease;
  transition-delay: 0.1s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.move-expand:hover span {
  opacity: 1;
}

.move-expand img {
  flex-shrink: 0;
}
</style>
